<template>
  <v-list-item
    :class="
      `courses-list-item courses-list-item--${
        isLibraryItem ? 'library' : 'my-course'
      } rounded-lg bhWhite mb-3 px-2`
    "
  >
    <v-list-item-avatar
      width="56"
      height="56"
      rounded
      :class="
        `courses-list-item__thumbnail mr-3 ${
          hasFile ? 'courses-list-item__thumbnail--selected' : ''
        }`
      "
    >
      <v-img
        :src="thumbnail"
        :lazy-src="require('@/assets/placeholder_other.png')"
        :alt="title"
        :key="`course-step-list-item-thumbnail-${id}-${thumbnailKey}`"
        @error="onThumbnailError"
        @click="showPreview"
      >
        <template v-slot:placeholder>
          <div class="d-flex align-center fill-height justify-center">
            <v-progress-circular
              indeterminate
              color="accent"
            ></v-progress-circular>
          </div>
        </template>
      </v-img>
    </v-list-item-avatar>
    <v-list-item-content class="course-list-item__block px-2">
      <a
        @click="$emit('navigate-to-course', id)"
        class="courses-list-item__column"
      >
        <v-list-item-title class="courses-list-item__title">{{
          title
        }}</v-list-item-title>
        <v-list-item-subtitle class="courses-list-item__description">{{
          description
        }}</v-list-item-subtitle>
      </a>
    </v-list-item-content>
    <v-list-item-content
      class="course-list-item__block px-2"
      v-if="!isLibraryItem || isAdvisor"
    >
      <avatars-group
        :max-count="3"
        :avatars-urls="participantsAvatars"
        :label="assignmentStatusLabel"
        color="bh-gray--6"
      >
        <template v-slot:after>
          <span class="ml-2">{{ participantsCount }} </span>
        </template>
      </avatars-group>
    </v-list-item-content>
    <v-list-item-content
      class="course-list-item__block px-2"
      v-if="!isLibraryItem"
    >
      <div class="d-flex align-center">
        <rating-stars
          color="#945EFF"
          :rating="ratingPercentage"
          :size="12"
        />&nbsp;<strong>{{ rating }}</strong>
      </div>
    </v-list-item-content>
    <v-list-item-content class="course-list-item__block px-2">
      <chip-select
        color="bh-gray--6"
        :items="COURSES_VISIBILITY_STATUS_OPTIONS"
        :value="visibilityStatus"
        :readonly="isLibraryItem"
        :overrideClick="true"
        @click="
          openChangeVisibility({ id, title, description, visibilityStatus })
        "
      />
    </v-list-item-content>
    <v-list-item-content class="course-list-item__block px-2">
      <p class="courses-list-item__label mb-0">{{ dateAdded }}</p>
      <p class="courses-list-item__label mb-0" v-if="creatorName">
        Created by {{ creatorName }}
      </p>
    </v-list-item-content>
    <v-list-item-action
      class="courses-list-item__actions d-flex flex-row align-center my-2"
    >
      <v-btn
        color="accent"
        elevation="0"
        text
        class="rounded-pill"
        @click="openAssignParticipants()"
        v-if="isLibraryItem && isAdvisor"
        >Assign</v-btn
      >
      <v-btn
        color="accent"
        elevation="0"
        text
        class="rounded-pill"
        @click="$emit('import-course', id)"
        v-if="isLibraryItem && isAdvisor"
        >Import</v-btn
      >
      <v-btn
        icon
        color="accent"
        elevation="0"
        @click="$emit('navigate-to-course', id)"
        v-else
      >
        <v-icon>mdi-arrow-right</v-icon>
      </v-btn>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
import RatingStars from '../exercises/RatingStars.vue'
import { mapGetters, mapState } from 'vuex'
import { COURSES_VISIBILITY_STATUS_OPTIONS } from '@/helpers/constants.js'
import ChipSelect from '@/components/inputs/ChipSelect.vue'
import AvatarsGroup from '@/components/avatars/AvatarsGroup.vue'
import { debounce } from '@/helpers/debounce'
import globalPreviewMediaMixin from '@/mixins/globalPreviewMedia'
import { getFileTypeByUrl } from '@/helpers/chat'
export default {
  components: { ChipSelect, AvatarsGroup, RatingStars },
  name: 'CoursesListItem',
  mixins: [globalPreviewMediaMixin],
  props: {
    isLibraryItem: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      require: true,
    },
    title: {
      type: String,
      require: true,
    },
    description: {
      type: String,
      default: '',
    },
    rating: {
      type: Number,
      default: 0,
    },
    createdBy: {
      type: Object,
      default: () => ({
        firstname: '',
        lastname: '',
      }),
    },
    createdDate: {
      type: String,
    },
    visibilityStatus: {
      validator(value) {
        return Object.values(COURSES_VISIBILITY_STATUS_OPTIONS)
          .map(el => el.value)
          .includes(value)
      },
    },
    assignmentStatus: {
      validator(value) {
        return ['ALL', 'SELECTED'].includes(value)
      },
    },
    participantsCount: {
      type: Number,
      default: 0,
    },
    participants: {
      type: Array,
      default: () => [],
    },
    thumbnailUrl: {
      type: String,
      default: '',
    },
    mediaUrl: {
      type: String,
      default: '',
    },
    staticMediaThumbnailUrl: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    COURSES_VISIBILITY_STATUS_OPTIONS,
    isThumbnailError: false,
    thumbnailKey: 0,
  }),
  computed: {
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ratingPercentage() {
      return Math.round(this.rating)
    },
    regDate() {
      // use moment
      return this.registrationDate
    },
    dateAdded() {
      return this.$moment(this.createdDate).format('MMMM, Do YYYY')
    },
    creatorName() {
      let name = ''
      if (this.createdBy.firstname) name += this.createdBy.firstname
      if (name !== '') name += ' '
      if (this.createdBy.lastname) name += this.createdBy.lastname
      return name
    },
    visibilityStatusName() {
      const currentVisibilityStatus = COURSES_VISIBILITY_STATUS_OPTIONS.find(
        el => el.value === this.visibilityStatus,
      )
      return currentVisibilityStatus?.text || 'Unknown'
    },
    participantsAvatars() {
      return this.participants.map(el => el.thumbnailUrl)
    },
    assignmentStatusLabel() {
      if (this.assignmentStatus === 'ALL') {
        return 'All'
      }
      return 'Selected'
    },
    thumbnail() {
      if (this.isThumbnailError) {
        const { type } = getFileTypeByUrl(this.thumbnailUrl || this.mediaUrl)
        if (type === 'image') {
          return require('@/assets/placeholder_image.png')
        }
        if (type === 'video') {
          return require('@/assets/placeholder_video.png')
        }
        if (type === 'pdf') {
          return require('@/assets/placeholder_pdf.png')
        }
        return require('@/assets/placeholder_other.png')
      }
      return (
        this.staticMediaThumbnailUrl ||
        this.thumbnailUrl ||
        this.mediaUrl ||
        require('@/assets/placeholder_other.png')
      )
    },
    hasFile() {
      return !!(
        this.staticMediaThumbnailUrl ||
        this.thumbnailUrl ||
        this.mediaUrl
      )
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
  },
  methods: {
    onThumbnailError() {
      if (this.thumbnailKey >= 3) {
        this.isThumbnailError = true
        return
      }
      debounce(
        () => {
          this.thumbnailKey++
        },
        5000,
        `REFRESH_COURSE_STEP_THUMBNAIL_${this.id}`,
      )
    },
    openChangeVisibility({
      id = null,
      title = null,
      description = null,
      visibilityStatus = null,
    }) {
      if (id === null) return

      this.$emit('open-change-visibility', {
        id,
        title,
        description,
        visibilityStatus,
      })
    },
    openAssignParticipants() {
      if (this.id === null) return

      this.$emit('open-assign-participants', {
        id: this.id,
        assignmentStatus: this.assignmentStatus,
      })
    },
    showPreview() {
      if (this.hasFile) {
        this.openGlobalPreviewMedia({
          mediaUrl:
            this.staticMediaThumbnailUrl || this.mediaUrl || this.thumbnailUrl,
          imageName: null,
          mlResourceUrl: null,
          fileTypeId: null,
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.courses-list-item {
  min-width: 950px;
  display: flex;
  align-items: center;

  &--library {
    min-width: 950px;
  }

  &__title {
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
  }

  &__description {
    font-size: 12px;
    line-height: 16px;
  }

  &__title,
  &__description {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: #000;
  }

  &__column {
    width: 100%;
  }

  &__label {
    font-size: 12px;
    line-height: 16px;
    color: $bh-gray--2;
  }

  &__thumbnail--selected {
    cursor: pointer;
  }

  .rating-stars .v-icon {
    margin-right: 5px;
  }
}
</style>
